<template>
  <SearchForm
    v-model="searchTerm"
    :disabled="disabled"
    :show-suggestions="(searchSuggestions.length > 0 || inCatalog.length > 0) && !!searchTerm"
    :options="searchCategories"
    :selected-option="selectedCategory"
    :is-input-focused="isSearchInputFocused"
    :placeholder="$t('recherche.rechercher-ean-titre-etc')"
    :show-adv-search="advancedSearchActive"
    @fetch-suggestions="fetchSearchSuggestion()"
    @submit="submitSearchForm($event)"
    @input-focused="isSearchInputFocused = $event"
    @select-option="defineSelectedCategory($event)"
  >
    <template #search-suggestions>
      <SearchSuggestions
        :search-suggestions="searchSuggestions"
        :in-catalog="inCatalog"
        :text-to-highlight="searchTerm"
        @close="isSearchInputFocused = false"
        @select="submitSearchForm($event)"
        @scope-change="fetchInCatalog($event)"
      />
    </template>
  </SearchForm>
</template>

<script>
import { mapGetters } from "vuex";

import {
  UilBookOpen,
  UilFileCheck,
  UilBookmark,
} from "@lde/vue-unicons";

import IconFileDevis from "@/components/icons/IconFileDevis.vue";
import IconNumerique from "@/components/icons/IconNumerique.vue";
import IconFourniture from "@/components/icons/IconFourniture.vue";
import IconPapier from "@/components/icons/IconPapier.vue";
import SearchForm from "@/components/search/SearchForm.vue";
import SearchSuggestions from "@/components/search/SearchSuggestions.vue";
import Api from "@/modules/axios";

/**
 * Champ de recherche principal du site.
 */
export default {
  name: "MainSearchForm",
  components: {
    SearchForm,
    SearchSuggestions,
  },
  props: {
    /**
     * Désactive l'input de recherche.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchSuggestions: [],
      inCatalog: [],
      searchTerm: "",
      isSearchInputFocused: false,
      selectedCategory: null,
      showAdvSearch: false,
    };
  },
  computed: {
    ...mapGetters([
      "suggestionFilter",
      "hasPerm",
      "hasPerms",
      "advancedSearchActive",
      "organismeActuel",
      "isMarcheDemo",
      "modeAjoutReference",
    ]),
    searchCategories() {
      const cat = [];
      const appendCategory = (category) => {
        if (!cat.some((existing) => existing.name === category.name)) {
          cat.push(category);
        }
      };
      const activeCatalogues = this.organismeActuel?.catalogues_actifs || [];

      activeCatalogues.forEach((catalogue) => {
        const formatted = this.formatCatalogueSearchCategory(catalogue);
        if (formatted) {
          appendCategory(formatted);
        }
      });

      // Fallback legacy : 3 onglets historiques si pas de catalogues actifs exposés.
      if (!cat.length) {
        if (this.hasPerm("view_articlepapier")) {
          appendCategory({
            label: this.$t("menu.livres-et-manuels-papier"),
            icon: IconPapier,
            name: "papiers",
            route: "catalogues_livres_manuels_papier",
            baseTab: "papiers",
          });
        }
        if (this.hasPerm("view_manuelnumerique")) {
          appendCategory({
            label: this.$t("menu.ressources-numeriques"),
            icon: IconNumerique,
            name: "numeriques",
            route: "catalogues_ressources_numeriques",
            baseTab: "numeriques",
          });
        }
        if (this.hasPerm("view_fourniture")) {
          appendCategory({
            label: this.$t("menu.fournitures-et-papeterie"),
            icon: IconFourniture,
            name: "fournitures",
            route: "catalogues_fournitures_papeterie",
            baseTab: "fournitures",
          });
        }
      }
      // Les catalogues actifs produisent des noms suffixés par le slug (ex. "numeriques__mon-catalogue") :
      // la dédup par `name` ne les rattraperait pas, d'où la comparaison sur `baseTab`.
      const hasCategoryForBaseTab = (baseTab) => cat.some((existing) => existing.baseTab === baseTab);

      if ((!this.isLogged || this.hasPerm("view_manuelnumerique")) && !hasCategoryForBaseTab("numeriques")) {
        appendCategory({
          label: this.$t("menu.ressources-numeriques"),
          icon: IconNumerique,
          name: "numeriques",
          route: "catalogues_ressources_numeriques",
          baseTab: "numeriques",
        });
      }
      if (this.hasPerm("view_fourniture") && !hasCategoryForBaseTab("fournitures")) {
        appendCategory({
          label: this.$t("menu.fournitures-et-papeterie"),
          icon: IconFourniture,
          name: "fournitures",
          route: "catalogues_fournitures_papeterie",
          baseTab: "fournitures",
        });
      }

      if (!this.modeAjoutReference) {
        if (this.hasPerm("view_liste")) {
          appendCategory({
            label: this.$t("menu.vos-listes"),
            icon: UilBookmark,
            name: "listes",
            route: "listes_devis_listes",
          });
        }
        if (this.hasPerm("view_devis")) {
          appendCategory({
            label: this.$t("menu.vos-devis"),
            icon: IconFileDevis,
            name: "devis",
            route: "listes_devis_devis",
          });
        }
        if (this.hasPerm("view_commande")) {
          appendCategory({
            label: this.$t("menu.vos-commandes"),
            icon: UilFileCheck,
            name: "commandes",
            route: "commandes_factures_commandes",
          });
        }
      }

      // Si l'utilisateur a accès à au moins deux catalogues, mettre "Tous les catlogues" en haut du menu.
      if (cat.length > 1) {
        cat.unshift({
          label: this.$t("menu.partout"),
          icon: UilBookOpen,
          name: "tous",
        });
      }

      return cat;

      // {
      //   label: "Listes",
      //   icon: UilFileBookmarkAlt,
      //   name: "listes",
      //   route: "listes_devis_listes",
      // },
      // {
      //   label: "Devis",
      //   icon: IconFileDevis,
      //   name: "devis",
      //   route: "listes_devis_devis",
      // },
      // {
      //   label: "Commandes",
      //   icon: UilFileCheck,
      //   name: "commandes",
      //   route: "commandes_factures_commandes",
      // },
      // {
      //   label: "Factures",
      //   icon: IconFileDevis,
      //   name: "factures",
      //   route: "commandes_factures_factures",
      // },
      // {
      //   label: this.$t("recherche.stocks"),
      //   icon: UilBox,
      //   name: "stocks",
      // }, {
      //   label: this.$t("general.etablissement"),
      //   icon: UilBuilding,
      //   name: "etablissement",
      // },
      // {
      //   label: this.$t("general.assistance"),
      //   icon: UilQuestionCircle,
      //   name: "assistance",
      // },
    },
  },
  watch: {
    $route: {
      handler() {
        // toujours fermer la recherche avancée si on change de route.
        this.showAdvSearch = false;

        // Mettre le catégorie par défaut si l'utilisateur ne l'a pas modifié.
        if (!this.selectedCategory) {
          this.selectedCategory = this.searchCategories[0];
        }
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Propose des suggestions selon la recherche effectuée.
     */
    fetchInCatalog() {
      // Par défaut, on prend le premier catalogue auquel l'utilisateur a accès pour les suggestions.
      let selectedCategory = this.selectedCategory;
      let catName = selectedCategory?.baseTab || selectedCategory?.name;
      if (catName === "tous") {
        selectedCategory = this.searchCategories.find((cat) => cat.name !== catName);
        catName = selectedCategory?.baseTab || selectedCategory?.name;
      }
      const params = {
        search: this.searchTerm,
        scope: this.suggestionFilter,
        cat: catName,
      };
      if (selectedCategory?.catalogueSlug) {
        params.catalogue_slug = selectedCategory.catalogueSlug;
      }
      Api().get("/recherche/custom_suggestions/", { params })
        .then(({ data }) => {
          this.inCatalog = data;
        });
    },
    /**
     * Récupération des suggestions de recherche
     */
    fetchSearchSuggestion() {
      Api().get(`/recherche/suggestions/?search=${this.searchTerm}`)
        .then(({ data }) => {
          if (data?.suggestions) {
            // Temporaire : À retirer une fois que l'on aura de vraies suggestions et non des pré-résultats.
            const searchSuggestions = data.suggestions.filter(
              (result) => result.nom_matiere !== "Résultats pertinents",
            );

            const relevantResults = data.suggestions.filter(
              (result) => result.nom_matiere === "Résultats pertinents",
            );

            // Merge des 2 tableaux pour n'en garder qu'un
            this.searchSuggestions = searchSuggestions.concat(relevantResults);
          }
          this.fetchInCatalog();
        })
        .catch((err) => {
          console.error(err);
        });
    },
    /**
     * Gestion de la soumission du formulaire.
     * Si la recherche saisie dans le formulaire n'est pas déjà présente
     * dans les recherches récentes, on l'ajoute via localStorage.
     * Si la longueur du tableau dépasse 7 éléments, on retire le dernier.
     */
    async submitSearchForm(searchTerms) {
      if (searchTerms) {
        this.searchTerm = searchTerms;
      }

      if (this.selectedCategory.name === "tous") {
        await this.$router.push({
          name: "search",
          query: { search: this.searchTerm },
        });
      } else {
        await this.$router.push({
          name: "search",
          query: { only: this.selectedCategory.name, search: this.searchTerm },
        });
      }

      // Reload la page
      this.$store.commit("addSearchKey");
      this.searchTerm = "";
    },
    /**
     * Récupère la catégorie sélectionnée.
     * @param {Object} category Infos de la catégorie.
     */
    defineSelectedCategory(category) {
      this.selectedCategory = category;
    },
    formatCatalogueSearchCategory(catalogue) {
      const typeToConfig = {
        papier: {
          perm: "view_articlepapier",
          icon: IconPapier,
          route: "catalogues_livres_manuels_papier",
          baseTab: "papiers",
          fallbackLabel: this.$t("menu.livres-et-manuels-papier"),
        },
        numerique: {
          perm: "view_manuelnumerique",
          icon: IconNumerique,
          route: "catalogues_ressources_numeriques",
          baseTab: "numeriques",
          fallbackLabel: this.$t("menu.ressources-numeriques"),
        },
        fourniture: {
          perm: "view_fourniture",
          icon: IconFourniture,
          route: "catalogues_fournitures_papeterie",
          baseTab: "fournitures",
          fallbackLabel: this.$t("menu.fournitures-et-papeterie"),
        },
      };

      const config = typeToConfig[catalogue?.type];
      if (!config || !this.hasPerm(config.perm)) {
        return null;
      }

      const slug = catalogue?.slug || "";
      return {
        label: catalogue?.nom || config.fallbackLabel,
        icon: config.icon,
        name: slug ? `${config.baseTab}__${slug}` : config.baseTab,
        route: config.route,
        catalogueSlug: slug || null,
        baseTab: config.baseTab,
      };
    },
  },
};
</script>
<style lang="scss"></style>
